import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Button, Card, Col, Collapse, DatePicker, Empty, Form, message, Row, Select, Skeleton, Spin, Table } from 'antd'
import moment from 'moment'
import AssetSearch from '../../components/AssetSearch'
import { sanitizeFieldName } from '../../util/methods'
import { fetchJobCardCostingReport } from '../../appRedux/actions/JobCard'
import { fetchAssetFiltersData, searchGlobalAssetData } from '../../appRedux/actions'
import { saveAs } from 'file-saver'
import json2csv from 'json2csv'
import { Link } from 'react-router-dom'

const { Option } = Select
const { RangePicker } = DatePicker
const { Panel } = Collapse

class JobCardCostingReport extends Component {
    constructor(props) {
        super(props)
        this.state = {
            selectedAssets: []
        }
    }

    componentDidMount() {
        this.props.fetchAssetFiltersData()
    }

    handleAssetSelect = (values) => {
        this.setState({
            selectedAssets: values || []
        })
    }

    downloadReport = () => {
        const { costingReportData } = this.props

        if (!costingReportData || costingReportData.length === 0) {
            message.warning('No data available for download')
            return
        }

        try {
            // Check if data is grouped
            const isGrouped = costingReportData && costingReportData.length > 0 && costingReportData[0] && costingReportData[0].groupKey !== undefined

            let allItems = []

            if (isGrouped) {
                // Combine all items from all groups
                costingReportData.forEach((group) => {
                    if (group.items && group.items.length > 0) {
                        allItems = allItems.concat(group.items)
                    }
                })
            } else {
                // Flat array - use as is
                allItems = costingReportData
            }

            if (allItems.length === 0) {
                message.warning('No items available for download')
                return
            }

            // Define CSV fields
            const fields = [
                { label: 'Job Card No', value: 'jobCardNo' },
                { label: 'Complaint No', value: 'complaintNo' },
                { label: 'Complaint Description', value: 'complaintDescription' },
                { label: 'Asset Code', value: 'assetCode' },
                { label: 'Location', value: 'location' },
                { label: 'Sub Location', value: 'client' },
                { label: 'Asset Type', value: 'assetType' },
                { label: 'Asset Sub Type', value: 'assetSubType' },
                { label: 'Asset Model', value: 'assetModel' },
                { label: 'Job Card Date', value: (row) => row.jobCardDate ? moment(row.jobCardDate).format('DD-MM-YYYY') : '' },
                { label: 'Status', value: 'status' },
                { label: 'Pending Invoice', value: 'statusTitle' },
                { label: 'Labour Cost', value: (row) => Number(row.totalLabourCost || 0).toFixed(2) },
                { label: 'Spares Cost', value: (row) => Number(row.totalSpareCost || 0).toFixed(2) },
                { label: 'Total Cost', value: (row) => (Number(row.totalLabourCost || 0) + Number(row.totalSpareCost || 0)).toFixed(2) }
            ]

            // Convert to CSV
            const csv = json2csv.parse(allItems, { fields })

            // Create blob and download
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
            const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss')
            saveAs(blob, `job-card-costing-report-${timestamp}.csv`)

            message.success(`Report downloaded successfully! ${allItems.length} records exported.`)
        } catch (error) {
            console.error('Error downloading report:', error)
            message.error('Failed to download report. Please try again.')
        }
    }

    handleSubmit = (e) => {
        e.preventDefault()
        e.stopPropagation()

        this.props.form.validateFields((err, values) => {
            if (err) {
                return console.log(err)
            }

            if (!values.periodFromTo || values.periodFromTo.length !== 2) {
                message.error('Period is required!')
                return
            }

            if (moment(values.periodFromTo[0]).isAfter(values.periodFromTo[1])) {
                message.error("Period From couldn't come later than Period To!")
                return
            }

            const payload = {
                periodFrom: moment(values.periodFromTo[0]).startOf('day').toISOString(),
                periodTo: moment(values.periodFromTo[1]).endOf('day').toISOString(),
                assetType: values.assetType && values.assetType !== 'all' ? values.assetType : undefined,
                assetModel: values.assetModel && values.assetModel !== 'all' ? values.assetModel : undefined,
                assetSubType: values.assetSubType && values.assetSubType !== 'all' ? values.assetSubType : undefined,
                location: values.location && values.location !== 'all' ? values.location : undefined,
                client: values.client && values.client !== 'all' ? values.client : undefined,
                assets: this.state.selectedAssets,
                summaryType: values.summaryType
            }

            this.props.fetchJobCardCostingReport(payload)
        })
    }

    render() {
        const { getFieldDecorator } = this.props.form
        const { loading, costingReportData, assetData, assetSearchLoading, authUser, assetFilters } = this.props

        const { assetTypes, assetSubTypes, assetModels } = assetFilters || {}

        // Table columns for the report
        const columns = [
            {
                title: 'Job Card Date',
                dataIndex: 'jobCardDate',
                key: 'jobCardDate',
                render: (date) => date ? moment(date).format('DD-MM-YYYY') : 'N/A'
            },
            {
                title: 'Job Card No',
                dataIndex: 'jobCardNo',
                key: 'jobCardNo',
                render: (text, record) => {
                    // Try to get jobCard ID from spareIssuedDetails or other nested data
                    const jobCardId = (record && record.jobCardId) || null

                    return jobCardId ? (
                        <Link to={`/job-card/report/${jobCardId}`}>{text}</Link>
                    ) : (text || 'N/A')
                }
            },
            {
                title: 'Asset Code',
                dataIndex: 'assetCode',
                key: 'assetCode',
                render: (text) => text || 'N/A'
            },
            {
                title: 'Complaint Details',
                dataIndex: 'complaintDescription',
                key: 'complaintDescription',
                render: (text) => text || 'N/A'
            },
            // {
            //     title: 'Complaint Date',
            //     dataIndex: 'complaintDate',
            //     key: 'complaintDate',
            //     render: (date) => date ? moment(date).format('DD-MM-YYYY') : 'N/A'
            // },
            {
                title: 'Labour Cost',
                dataIndex: 'totalLabourCost',
                key: 'totalLabourCost',
                render: (amount) => {
                    const numAmount = Number(amount) || 0
                    return numAmount.toLocaleString('en-IN', {
                        maximumFractionDigits: 2,
                        style: 'currency',
                        currency: 'INR'
                    })
                }
            },
            {
                title: 'Spares Cost',
                dataIndex: 'totalSpareCost',
                key: 'totalSpareCost',
                render: (amount) => {
                    const numAmount = Number(amount) || 0
                    return numAmount.toLocaleString('en-IN', {
                        maximumFractionDigits: 2,
                        style: 'currency',
                        currency: 'INR'
                    })
                }
            },
            {
                title: 'Total Cost',
                dataIndex: 'totalAmount',
                key: 'totalAmount',
                render: (_, record) => {
                    const total = (Number(record.totalLabourCost) || 0) + (Number(record.totalSpareCost) || 0)
                    return total.toLocaleString('en-IN', {
                        maximumFractionDigits: 2,
                        style: 'currency',
                        currency: 'INR'
                    })
                }
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (text) => text || 'N/A'
            },
            {
                title: 'Pending Invoice',
                dataIndex: 'statusTitle',
                key: 'statusTitle',
                render: (statusTitle) => statusTitle ? statusTitle === 'ClosedPendingInvoice' ? 'Yes' : 'No' : 'N/A'
            },
            // {
            //     title: 'Asset Type',
            //     dataIndex: 'assetType',
            //     key: 'assetType',
            //     render: (text) => text || 'N/A'
            // },
            // {
            //     title: 'Asset Sub Type',
            //     dataIndex: 'assetSubType',
            //     key: 'assetSubType',
            //     render: (text) => text || 'N/A'
            // },
            // {
            //     title: 'Model',
            //     dataIndex: 'assetModel',
            //     key: 'assetModel',
            //     render: (text) => text || 'N/A'
            // },
            // {
            //     title: 'Location',
            //     dataIndex: 'location',
            //     key: 'location',
            //     render: (text) => text || 'N/A'
            // },
            // {
            //     title: 'Sub Location',
            //     dataIndex: 'client',
            //     key: 'client',
            //     render: (text) => text || 'N/A'
            // },
        ]

        // Check if data is grouped (has groupKey/groupName) or flat array
        const isGrouped = costingReportData && costingReportData.length > 0 && costingReportData[0] && costingReportData[0].groupKey !== undefined

        // Prepare table data - if grouped, each group has parent object and items array
        let groupedData = []
        let flatData = []

        if (isGrouped) {
            // Data is grouped: array of group objects with groupKey, groupName, totals, and items array
            groupedData = costingReportData.map((group, groupIndex) => {
                return {
                    key: `group-${groupIndex}`,
                    groupKey: group.groupKey,
                    groupName: group.groupName || group.groupKey,
                    totalSpareCost: Number(group.totalSpareCost || 0),
                    totalLabourCost: Number(group.totalLabourCost || 0),
                    dateFrom: group.dateFrom,
                    dateTo: group.dateTo,
                    records: (group.items || []).map((item, index) => ({
                        key: `group-${groupIndex}-record-${index}`,
                        ...item
                    }))
                }
            })
        } else {
            // Data is flat array (for 'Details' summary type)
            flatData = costingReportData && costingReportData.length > 0
                ? costingReportData.map((item, index) => ({
                    key: index,
                    ...item
                }))
                : []
        }

        return (
            <Spin spinning={loading}>
                <Card className='gx-card-metrics' title='Asset Maintenance Cost Report'>
                    <Form onSubmit={this.handleSubmit} className='ant-advanced-search-form' layout='inline' labelAlign='left'>
                        <Row gutter={8}>
                            <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                                <Form.Item key={'location'} label={'Location'} style={{ width: '100%' }}>
                                    {getFieldDecorator(`location`, {
                                        rules: []
                                    })(<Select
                                        showSearch
                                        showArrow
                                        filterOption={(input, option) =>
                                            (option.props.children + "").toLowerCase().indexOf((input + "").toLowerCase()) >= 0
                                        }
                                        placeholder={`Select Location`}
                                    >
                                        <Select.Option key={'AllLocation'}
                                            value={'all'}>All</Select.Option>
                                        {(authUser && authUser.locations || []).map(item =>
                                            <Select.Option key={item._id}
                                                value={item._id}>{item.name}</Select.Option>)}
                                    </Select>)
                                    }
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                                <Form.Item key={'client'} label={'Sub Location'} style={{ width: '100%' }}>
                                    {getFieldDecorator(`client`, {
                                        rules: []
                                    })(<Select
                                        showSearch
                                        showArrow
                                        filterOption={(input, option) =>
                                            (option.props.children + "").toLowerCase().indexOf((input + "").toLowerCase()) >= 0
                                        }
                                        placeholder={`Select Sub Location`}
                                    >
                                        <Select.Option key={'AllClient'}
                                            value={'all'}>All</Select.Option>
                                        {(authUser && authUser.clients || []).map(item =>
                                            <Select.Option key={item._id}
                                                value={item._id}>{item.name}</Select.Option>)}
                                    </Select>)
                                    }
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                                <Form.Item
                                    key={"periodFromTo"}
                                    label={"Select Period"}
                                    style={{ width: "100%" }}
                                >
                                    {getFieldDecorator(`periodFromTo`, {
                                        rules: [
                                            { required: true, message: `Period is required!` }
                                        ],
                                        initialValue: null
                                    })(<RangePicker style={{ width: "100%" }} />)}
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                                <Form.Item
                                    key={"assetType"}
                                    label={"Asset Type"}
                                    style={{ width: "100%" }}
                                >
                                    {getFieldDecorator(`assetType`, {
                                        onChange: () => {
                                            // Reset subType and model when type changes
                                            this.props.form.setFieldsValue({
                                                assetSubType: 'all',
                                                assetModel: 'all'
                                            })
                                        }
                                    })(
                                        <Select
                                            showSearch
                                            showArrow
                                            filterOption={(input, option) =>
                                                (option.props.children + "").toLowerCase().indexOf((input + "").toLowerCase()) >= 0
                                            }
                                            placeholder={`Select Asset Type`}
                                        >
                                            <Select.Option key={'AllType'}
                                                value={'all'}>All</Select.Option>
                                            {(assetTypes || []).map(item =>
                                                <Select.Option key={item._id}
                                                    value={item._id}>{item.name}</Select.Option>)}
                                        </Select>
                                    )}
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                                <Form.Item
                                    key={"assetSubType"}
                                    label={"Asset Sub Type"}
                                    style={{ width: "100%" }}
                                >
                                    {getFieldDecorator(`assetSubType`, {
                                        onChange: () => {
                                            // Reset model when subType changes
                                            this.props.form.setFieldsValue({
                                                assetModel: 'all'
                                            })
                                        }
                                    })(
                                        <Select
                                            showSearch
                                            showArrow
                                            filterOption={(input, option) =>
                                                (option.props.children + "").toLowerCase().indexOf((input + "").toLowerCase()) >= 0
                                            }
                                            placeholder={`Select Asset Sub Type`}
                                        >
                                            <Select.Option key={'AllSubType'}
                                                value={'all'}>All</Select.Option>
                                            {(assetSubTypes || [])
                                                .filter(item => {
                                                    const selectedAssetType = this.props.form.getFieldValue('assetType')
                                                    return selectedAssetType === 'all' || item.assetType === selectedAssetType
                                                })
                                                .map(item =>
                                                    <Select.Option key={item._id}
                                                        value={item._id}>{item.name}</Select.Option>)}
                                        </Select>
                                    )}
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                                <Form.Item
                                    key={"assetModel"}
                                    label={"Asset Model"}
                                    style={{ width: "100%" }}
                                >
                                    {getFieldDecorator(`assetModel`)(
                                        <Select
                                            showSearch
                                            showArrow
                                            filterOption={(input, option) =>
                                                (option.props.children + "").toLowerCase().indexOf((input + "").toLowerCase()) >= 0
                                            }
                                            placeholder={`Select Asset Model`}
                                        >
                                            <Select.Option key={'AllModel'}
                                                value={'all'}>All</Select.Option>
                                            {(assetModels || [])
                                                .filter(item => {
                                                    const selectedAssetType = this.props.form.getFieldValue('assetType')
                                                    const selectedAssetSubType = this.props.form.getFieldValue('assetSubType')

                                                    // If both type and subtype are 'all', show all models
                                                    if (selectedAssetType === 'all' && selectedAssetSubType === 'all') return true

                                                    // If subtype is selected (regardless of asset type), filter by subtype
                                                    if (selectedAssetSubType !== 'all') {
                                                        return item.assetSubType === selectedAssetSubType
                                                    }

                                                    // If only asset type is selected (subtype is 'all'), show models for all subtypes of selected type
                                                    if (selectedAssetType !== 'all') {
                                                        const validSubTypes = assetSubTypes
                                                            .filter(st => st.assetType === selectedAssetType)
                                                            .map(st => st._id)
                                                        return validSubTypes.includes(item.assetSubType)
                                                    }

                                                    return true
                                                })
                                                .map(item =>
                                                    <Select.Option key={item._id}
                                                        value={item._id}>{item.name}</Select.Option>)}
                                        </Select>
                                    )}
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                                <Form.Item key={'asset'} label={'Search Asset'} style={{ width: '100%' }}>
                                    {getFieldDecorator(`asset`, {
                                        rules: []
                                    })(<AssetSearch
                                        styleName="gx-lt-icon-search-bar-lg"
                                        placeholder="Or search any Asset..."
                                        onChange={this.props.searchGlobalAssetData}
                                        assetData={
                                            assetData && assetData.filter((ast) => ast.assetModel)
                                        }
                                        onSelect={this.handleAssetSelect}
                                        authUser={authUser}
                                        loading={assetSearchLoading}
                                        multiple={true}
                                    />)}
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                                <Form.Item key='summaryType' label='Summary Type' style={{ width: '100%' }}>
                                    {getFieldDecorator('summaryType', {
                                        rules: [],
                                        initialValue: ''
                                    })(
                                        <Select placeholder='Select Summary Type' style={{ width: '100%' }}>
                                            <Option value=''>Details</Option>
                                            <Option value='location'>Location</Option>
                                            <Option value='client'>Sub Location</Option>
                                            <Option value='assetModel'>Asset Model</Option>
                                            <Option value='assetType'>Asset Type</Option>
                                            <Option value='subType'>Asset Sub Type</Option>
                                            <Option value='asset'>Asset</Option>
                                        </Select>
                                    )}
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                                <Form.Item style={{ marginTop: '25px' }}>
                                    <span style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                                        <Button type="primary" htmlType="submit" loading={loading}>
                                            Generate Report
                                        </Button>
                                        <Button
                                            disabled={!costingReportData || costingReportData.length === 0 || loading}
                                            type="default"
                                            htmlType="button"
                                            icon={'download'}
                                            onClick={this.downloadReport}
                                        >
                                            Download
                                        </Button>
                                    </span>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>

                    {isGrouped && groupedData.length > 0 ? (
                        <div style={{ marginTop: '20px' }}>
                            <Collapse bordered={false}>
                                {groupedData.map((group) => {
                                    const { groupName, totalSpareCost, totalLabourCost, dateFrom, dateTo, records } = group
                                    const totalCost = totalLabourCost + totalSpareCost
                                    const dateRange = dateFrom && dateTo
                                        ? `${moment(dateFrom).format('DD-MM-YYYY')} to ${moment(dateTo).format('DD-MM-YYYY')}`
                                        : dateFrom
                                            ? moment(dateFrom).format('DD-MM-YYYY')
                                            : groupName

                                    return (
                                        <Panel
                                            key={group.key}
                                            header={
                                                <Row className='job-card-row'>
                                                    <Col xs={24} sm={12} md={6} lg={4}>
                                                        <div className='job-card-details'>
                                                            <div className='content-row-style'>{groupName}</div>
                                                        </div>
                                                    </Col>
                                                    {dateFrom && (
                                                        <Col xs={24} sm={12} md={6} lg={4}>
                                                            <div className='job-card-details'>
                                                                <div className='content-row-style'>Period: {dateRange}</div>
                                                            </div>
                                                        </Col>
                                                    )}
                                                    <Col xs={24} sm={12} md={6} lg={4}>
                                                        <div className='job-card-details'>
                                                            <div className='content-row-style'>Total Labour Cost: {totalLabourCost.toLocaleString('en-IN', {
                                                                maximumFractionDigits: 2,
                                                                style: 'currency',
                                                                currency: 'INR'
                                                            })}</div>
                                                        </div>
                                                    </Col>
                                                    <Col xs={24} sm={12} md={6} lg={4}>
                                                        <div className='job-card-details'>
                                                            <div className='content-row-style'>Total Spare Cost: {totalSpareCost.toLocaleString('en-IN', {
                                                                maximumFractionDigits: 2,
                                                                style: 'currency',
                                                                currency: 'INR'
                                                            })}</div>
                                                        </div>
                                                    </Col>
                                                    <Col xs={24} sm={12} md={6} lg={4}>
                                                        <div className='job-card-details'>
                                                            <div className='content-row-style'>Total Cost: {totalCost.toLocaleString('en-IN', {
                                                                maximumFractionDigits: 2,
                                                                style: 'currency',
                                                                currency: 'INR'
                                                            })}</div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            }
                                        >
                                            <Table
                                                columns={columns}
                                                dataSource={records}
                                                pagination={false}
                                                bordered
                                                scroll={{ x: 'max-content' }}
                                            />
                                        </Panel>
                                    )
                                })}
                            </Collapse>
                        </div>
                    ) : flatData && flatData.length > 0 ? (
                        <div style={{ marginTop: '20px' }}>
                            <Table
                                columns={columns}
                                dataSource={flatData}
                                pagination={{ pageSize: 10 }}
                                bordered
                                scroll={{ x: 'max-content' }}
                            />
                        </div>
                    ) : loading ? (
                        <Skeleton active />
                    ) : costingReportData && costingReportData.length === 0 ? (
                        <div style={{ marginTop: '20px', textAlign: 'center', padding: '20px' }}>
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data found for the selected criteria" />
                        </div>
                    ) : null}
                </Card>
            </Spin>
        )
    }
}

const mapStateToProps = ({ jobCard, commonData, auth }) => {
    return {
        loading: commonData.loading,
        costingReportData: jobCard.costingReportData || [],
        assetData: commonData.assetSearchData || [],
        assetSearchLoading: commonData.assetSearchLoading || false,
        authUser: auth.authUser,
        assetFilters: commonData.assetFilters
    }
}

function mapDispatchToProps(dispatch) {
    return {
        fetchAssetFiltersData,
        searchGlobalAssetData,
        fetchJobCardCostingReport,
    }
}

const CostingReportForm = Form.create({
    name: 'job_card_costing_report',
    // onFieldsChange(props, changedFields) {
    //     props.onChange(changedFields)
    // }
})(JobCardCostingReport)

export default connect(mapStateToProps, mapDispatchToProps())(CostingReportForm)

